import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import BookSkeleton from './BookSkeleton';
import useUser from '../../utility/useUser';

const SORT_OPTIONS = {
  DEFAULT: 'default',
  PRICE_LOW_TO_HIGH: 'price_low_to_high',
  PRICE_HIGH_TO_LOW: 'price_high_to_low'
};

const ProductList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.DEFAULT);
  const { books, totalPages, currentPage, booksLoading, error, fetchBooks, searchQuery } = useUser();
  const [displayedBooks, setDisplayedBooks] = useState([]);

  useEffect(() => {
    setDisplayedBooks(books || []);
  }, [books]);

  useEffect(() => {
    let sortedBooks = [...(books || [])];
    
    switch (sortBy) {
      case SORT_OPTIONS.PRICE_LOW_TO_HIGH:
        sortedBooks.sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
        break;
      case SORT_OPTIONS.PRICE_HIGH_TO_LOW:
        sortedBooks.sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
        break;
      default:
        // Keep original order
        break;
    }
    
    setDisplayedBooks(sortedBooks);
  }, [books, sortBy]);

  const handleSearch = async (query) => {
    await fetchBooks(1, query);
  };

  const handlePageChange = (page) => {
    fetchBooks(page, searchQuery);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const handleBookClick = (bookId, event) => {
    if (event.target.tagName.toLowerCase() === 'button') {
      return;
    }
    navigate(`/books/${bookId}`);
  };

  const handleBuyClick = (event) => {
    event.stopPropagation();
    navigate('/app-download');
  };

  return (
    <div className="container g-pt-70--md g-pb-30">
      <div className="p-3 row mt-4">
        <div className="col-md-10 mx-auto">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            {/* <BooksTabs /> */}
            <div className="d-flex flex-wrap">
              <SearchBar 
                onSearch={handleSearch} 
                isLoading={booksLoading}
                initialQuery={searchQuery}
              />
              {error && (
                <div className="text-danger small ml-2">
                  {error}
                </div>
              )}
              <div className='sort-by-container mt-4'>
                <h2 className="h6 align-middle d-inline-block g-font-weight-400 text-uppercase g-pos-rel g-top-1 mb-0">{t('sortBy')}:</h2>
                <select 
                  className="custom-select" 
                  value={sortBy} 
                  onChange={handleSortChange}
                >
                  {[
                    { id: 'default', label: t('default'), value: SORT_OPTIONS.DEFAULT },
                    { id: 'low-to-high', label: t('priceLowToHigh'), value: SORT_OPTIONS.PRICE_LOW_TO_HIGH },
                    { id: 'high-to-low', label: t('priceHighToLow'), value: SORT_OPTIONS.PRICE_HIGH_TO_LOW }
                  ].map((option) => (
                    <option key={option.id} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="row g-pt-30 g-mb-20">
            {booksLoading ? (
              // Show 8 skeleton loaders while loading
              Array.from({ length: 8 }).map((_, index) => (
                <BookSkeleton key={index} />
              ))
            ) : (
              displayedBooks.map((book) => (
                <div key={book.id} className="col-12 col-md-6 col-lg-3 g-mb-30">
                  <div 
                    className="u-shadow-v20 g-bg-white rounded g-pa-20"
                    onClick={(e) => handleBookClick(book.id, e)}
                    style={{ 
                      cursor: 'pointer',
                      height: '400px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <figure className="g-pos-rel g-mb-20" style={{ height: '200px', overflow: 'hidden' }}>
                      <img 
                        className="img-fluid" 
                        src={book.image_url} 
                        alt={book.title}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain',
                          objectPosition: 'center'
                        }}
                      />
                      {book.tag && (
                        <figcaption className="w-100 g-bg-primary g-bg-black--hover text-center g-pos-abs g-bottom-0 g-transition-0_2 g-py-5">
                          {book.tag}
                        </figcaption>
                      )}
                      {book.discount && (
                        <span className="u-ribbon-v1 g-width-40 g-height-40 g-color-white g-bg-primary g-font-size-13 text-center text-uppercase g-rounded-50x g-top-10 g-right-minus-10 g-px-2 g-py-10">
                          {book.discount}
                        </span>
                      )}
                    </figure>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div className="d-flex flex-column" style={{ flex: 1 }}>
                        <h4 className="h6 g-color-black mb-1" style={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          height: '2.5em'
                        }}>
                          <span className="u-link-v5 g-color-black g-color-primary--hover">
                            {book.title}
                          </span>
                        </h4>
                        <span className="d-inline-block g-color-gray-dark-v5 g-font-size-13 mb-2" style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {book.authors}
                        </span>
                        <span className="d-block g-color-black g-font-size-17 mb-3">
                          {book.price} zł
                        </span>
                        <button
                          onClick={handleBuyClick}
                          className="btn btn-primary btn-block mt-auto"
                        >
                          {t('buyInApp')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <Pagination totalPages={totalPages} onPageChange={handlePageChange} currentPage={currentPage} />
        </div>
      </div>
    </div>
  );
};

export default ProductList;
